import React, { Fragment, useContext } from "react"
import { MobergButton, MobergButtonVariant, MobergDropdown, MobergInputLabel, MobergTheme } from "../../../../../Moberg"
import {
	DEFAULT_DSA_DISPLAY_SCALE,
	DEFAULT_QEEG_CHANNEL_GROUP_ID,
	DSADisplayScale,
	getQEEGDSATraceName,
	isQEEGDSAAnalysis,
	QEEGChannelGroupId,
} from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import {
	DEFAULT_SPECTROGRAM_TRACE_SETTINGS,
	getSpectrogramDataKey,
	RenderStrategy,
	SpectrogramTraceConfigJSON,
	TraceConfigJSON,
	TraceOption,
} from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { QEEGChannelGroupDropdown } from "../QEEGChannelGroupDropdown"
import { SpectrogramGraphModalitiesContext, SpectrogramTraceSettingsCells, useReasonAddSpectrogramTraceIsDisabled } from "./SpectrogramTraceSettings"

const getDefaultDSASpectrogramSettings = (
	channelGroupId: QEEGChannelGroupId
): Pick<SpectrogramTraceConfigJSON, "name" | "displayScale" | "colorSpectrum" | "lowerBound" | "upperBound"> => ({
	name: getQEEGDSATraceName(channelGroupId),
	...DEFAULT_SPECTROGRAM_TRACE_SETTINGS,
})

export const AnalyticSpectrogramModalities: React.FC = () => {
	const { traceOptions, updateGraphProperty } = useContext(ConfigureWindowModalContext)
	const { activeAnalyticSpectrogramTraces, updateTrace } = useContext(SpectrogramGraphModalitiesContext)

	const spectrogramOptions = traceOptions.filter((option: TraceOption) =>
		option.value.onDemandAnalysis && option.supportedRenderStrategies.includes(RenderStrategy.SPECTROGRAM)
	)
	const reasonAddTraceIsDisabled = useReasonAddSpectrogramTraceIsDisabled(spectrogramOptions, "No spectrogram analytics available.")

	const getTraceChannelGroupId = (trace: TraceOption["value"]): QEEGChannelGroupId => {
		return isQEEGDSAAnalysis(trace.onDemandAnalysis)
			? trace.onDemandAnalysis.channelGroupId
			: DEFAULT_QEEG_CHANNEL_GROUP_ID
	}

	const buildSpectrogramTraceBase = (
		baseTrace: TraceOption["value"],
		displayScale: DSADisplayScale,
		channelGroupId?: QEEGChannelGroupId
	): TraceOption["value"] => {
		const onDemandAnalysis = isQEEGDSAAnalysis(baseTrace.onDemandAnalysis)
			? {
				...baseTrace.onDemandAnalysis,
				channelGroupId: channelGroupId ?? baseTrace.onDemandAnalysis.channelGroupId,
				displayScale,
			}
			: baseTrace.onDemandAnalysis

		return {
			...baseTrace,
			onDemandAnalysis,
			dataKey: getSpectrogramDataKey(baseTrace.rawDataKey, onDemandAnalysis),
		}
	}

	const handleAddTrace = () => {
		const defaultOption = spectrogramOptions[0]

		if (!defaultOption) {
			return
		}

		const displayScale = DEFAULT_DSA_DISPLAY_SCALE
		const channelGroupId = getTraceChannelGroupId(defaultOption.value)
		const baseTrace = buildSpectrogramTraceBase(defaultOption.value, displayScale, channelGroupId)
		const newTrace: SpectrogramTraceConfigJSON = {
			...baseTrace,
			...getDefaultDSASpectrogramSettings(channelGroupId),
			id: `trace-${new Date(Date.now()).toISOString()}`,
			renderStrategy: RenderStrategy.SPECTROGRAM,
			isVisible: true,
		}

		updateGraphProperty("traces", (previous: TraceConfigJSON[] = []) => [...previous, newTrace])
	}

	const handleAnalyticChange = (traceId: string, newBase: TraceOption["value"]) => {
		const existingTrace = activeAnalyticSpectrogramTraces.find(t => t.id === traceId)
		if (existingTrace) {
			const displayScale = DEFAULT_DSA_DISPLAY_SCALE
			const channelGroupId = getTraceChannelGroupId(newBase)
			const baseTrace = buildSpectrogramTraceBase(newBase, displayScale, channelGroupId)
			updateTrace(traceId, {
				...baseTrace,
				...getDefaultDSASpectrogramSettings(channelGroupId),
			})
		}
	}

	const handleChannelGroupChange = (traceId: string, channelGroupId: QEEGChannelGroupId) => {
		const existingTrace = activeAnalyticSpectrogramTraces.find(t => t.id === traceId)
		if (!existingTrace || !isQEEGDSAAnalysis(existingTrace.onDemandAnalysis)) {
			return
		}

		const defaults = getDefaultDSASpectrogramSettings(channelGroupId)
		updateTrace(traceId, {
			...buildSpectrogramTraceBase(existingTrace, defaults.displayScale, channelGroupId),
			...defaults,
		})
	}

	const handleDisplayScaleChange = (traceId: string, displayScale: DSADisplayScale) => {
		const existingTrace = activeAnalyticSpectrogramTraces.find(t => t.id === traceId)
		if (!existingTrace) {
			return
		}

		updateTrace(traceId, {
			...buildSpectrogramTraceBase(existingTrace, displayScale),
			displayScale,
		})
	}

	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "180px 180px 150px 180px 180px 120px 120px 120px 80px 48px",
				columnGap: "16px",
				rowGap: "8px",
				padding: "0 16px",
				alignItems: "center",
				maxWidth: "fit-content",
			}}
		>
			<MobergInputLabel text="Analytic" />
			<MobergInputLabel text="Channel group" />
			<MobergInputLabel text="Label" />
			<MobergInputLabel text="Display scale" />
			<MobergInputLabel text="Color spectrum" />
			<MobergInputLabel text="Lower bound" />
			<MobergInputLabel text="" />
			<MobergInputLabel text="Upper bound" />
			<MobergInputLabel text="Visible" />
			<MobergInputLabel text="" />

			{activeAnalyticSpectrogramTraces.map(trace => (
				<Fragment key={trace.id}>
					<MobergDropdown
						dataTestId="spectrogram-analytic-select"
						options={spectrogramOptions}
						selectedValue={spectrogramOptions.find(option => option.value.rawDataKey === trace.rawDataKey)?.value ?? spectrogramOptions[0]?.value}
						onChange={newBase => handleAnalyticChange(trace.id, newBase)}
						equals={(a, b) => a.rawDataKey === b.rawDataKey && a.dataSource === b.dataSource}
						width={175}
					/>

					<QEEGChannelGroupDropdown
						dataTestId="spectrogram-channel-group-select"
						selectedValue={getTraceChannelGroupId(trace)}
						onChange={channelGroupId => handleChannelGroupChange(trace.id, channelGroupId)}
					/>

					<SpectrogramTraceSettingsCells
						trace={trace}
						testIdPrefix="spectrogram"
						onDisplayScaleChange={displayScale => handleDisplayScaleChange(trace.id, displayScale)}
					/>
				</Fragment>
			))}

			<MobergButton
				dataTestId="spectrogram-add-analytic-button"
				onClick={handleAddTrace}
				theme={MobergTheme.BLUE}
				variant={MobergButtonVariant.OUTLINE}
				style={{ width: "175px" }}
				disabled={!!reasonAddTraceIsDisabled}
				tooltip={reasonAddTraceIsDisabled}
			>
				Add analytic
			</MobergButton>
		</div>
	)
}
